import { BLOCKS } from '@contentful/rich-text-types';
import clsx from 'clsx';
import { Link } from 'next-view-transitions';
import React from 'react';

import { renderTextWithMarks } from '@/app/components/ui/RichText/Configs/renderTextMarks';

// Forward declaration for renderList as renderListItem calls it
// eslint-disable-next-line prefer-const
let renderListFn: (node: any, ordered: boolean, isNested?: boolean, locationData?: string) => React.ReactNode;

// Helper function to render text with marks

const renderListItem = (node: any, index: number, ordered: boolean, isNested: boolean, locationData?: string): React.ReactNode => {
  let customMarkerText: string = '';
  if (isNested) {
    if (ordered) {
      customMarkerText = String.fromCharCode(65 + index) + '.'; // e.g., A., B.
    } else {
      customMarkerText = '‣'; // Custom marker for nested unordered lists
    }
  }

  const firstParagraphNode = node.content.find((cn: any) => cn.nodeType === BLOCKS.PARAGRAPH);
  const firstTextValueInParagraph = firstParagraphNode?.content[0]?.value;
  const keyContentPart = firstTextValueInParagraph?.slice(0, 20) || `item-${index}`;
  const markerForKey = customMarkerText ? customMarkerText.replace('.', '') : ordered ? 'ol' : 'ul';
  const key: string = `${index}-${isNested ? markerForKey : ordered ? 'ol' : 'ul'}-${keyContentPart}`;

  // Add whitespace preservation for line breaks, especially important for legal content
  const isLegalContent = locationData === 'legal';

  return (
    <li
      key={key}
      className={clsx(
        'w-full pb-3 text-RichText-Body font-normal text-WHITE/90 md:text-Body-Large sm:text-Body',
        { 'marker:text-RichText-Body marker:md:text-Body-Large marker:sm:text-[0.9rem]': !isNested },
        { 'pt-3 last:pb-0': isNested },
        // Preserve line breaks within list items, especially important for legal content
        { 'whitespace-pre-wrap': isLegalContent || ordered },
        // Add better line height for legal content readability
        { 'leading-relaxed': isLegalContent }
      )}
    >
      {isNested && customMarkerText && (
        <span className={clsx('mr-2 inline-block', { 'w-5 text-left sm:w-[20px]': ordered })} aria-hidden="true">
          {customMarkerText}
        </span>
      )}
      {node.content.map((contentNode: any, contentIndex: number) => {
        const contentKey = `${key}-content-${contentIndex}`;
        if (contentNode.nodeType === BLOCKS.PARAGRAPH) {
          return (
            <React.Fragment key={contentKey}>
              {contentNode.content.map((item: any, itemIndex: number) => (
                <React.Fragment key={`${contentKey}-item-${itemIndex}`}>
                  {item.nodeType === 'hyperlink' ? (
                    <Link
                      href={item.data.uri}
                      target="_blank"
                      className="text-BLUE-400 underline transition-all duration-300 hover:text-BLUE-400/80 focus:text-BLUE-400/80 active:text-BLUE-400/60"
                    >
                      {item.content[0].value}
                    </Link>
                  ) : (
                    renderTextWithMarks(item, locationData)
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        } else if (contentNode.nodeType === BLOCKS.OL_LIST || contentNode.nodeType === BLOCKS.UL_LIST) {
          // Use the correctly scoped renderListFn
          const nestedListElement = renderListFn(
            contentNode,
            contentNode.nodeType === BLOCKS.OL_LIST,
            true,
            locationData,
          ) as React.ReactElement;
          // The nested list element itself needs a key when it's a child of this map.
          if (React.isValidElement(nestedListElement)) {
            return React.cloneElement(nestedListElement, { key: contentKey });
          }
          // Fallback or error handling if nestedListElement isn't what we expect, though it should be an element.
          return null;
        }
        return null;
      })}
    </li>
  );
};

const renderList = (node: any, ordered: boolean, isNested: boolean = false, locationData?: string): React.ReactNode => {
  const Tag: 'ol' | 'ul' = ordered ? 'ol' : 'ul';
  const isLegalContent = locationData === 'legal';

  return (
    <Tag
      className={clsx('col-span-full h-full w-full pb-2 pl-8', {
        'list-decimal': ordered && !isNested,
        'list-disc': !ordered && !isNested,
        'list-none': isNested,
        // Add better spacing for legal content
        'space-y-2': isLegalContent && ordered,
      })}
    >
      {node.content.map((item: any, index: number) => renderListItem(item, index, ordered, isNested, locationData))}
    </Tag>
  );
};

// Assign the function to the forward-declared variable
renderListFn = renderList;

export { renderList };
